import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chip, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Checkbox, Box, Typography
} from '@mui/material';
import { useDropdownContext } from './DropdownContext';
import useLongDateTime from 'app/hooks/useLongDateTime';
import { sortByCreationDate } from 'app/utilities/SortBy';

export const DropdownList = <T extends string | number>() => {
  const { t } = useTranslation();

  const { value, handleToggle, filteredOptions } = useDropdownContext<T>();

  const [, formatDateTime] = useLongDateTime({
    displayTime: true,
    local: true
  });

  const sortedOptions = filteredOptions.sort((a, b) => {
    const docA = a.props?.document;
    const docB = b.props?.document;
    if (!docA || !docB) return 0;
    return sortByCreationDate(docA, docB, 'desc');
  });

  return (
    <List sx={{ width: '100%', maxHeight: 540, overflowY: 'auto' }}>
      {sortedOptions.length === 0 ? (
        <Box p={2}><Typography variant="body2" color="text.secondary">No results found</Typography></Box>
      ) : (
        sortedOptions.map((option) => {
          const labelId = `checkbox-list-label-${option.id}`;
          const isSelected = value.includes(option.id);
          const createdOnFormatted = option.props?.document.createdOn
            ? formatDateTime(option.props.document.createdOn)
            : null;

          return (
            <ListItem key={option.id} disablePadding>
              <ListItemButton role={undefined} onClick={(e) => handleToggle(option.id, e)} dense>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    tabIndex={-1}
                    edge="start"
                    checked={isSelected}
                    disableRipple
                    slotProps={{
                      input: { 'aria-labelledby': labelId }
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  id={labelId}
                  primary={
                    <Fragment>
                      <Typography color="CaptionText" variant="body2">
                        <Typography
                          title={option.props?.document.name}
                          display="block"
                          variant="subtitle2"
                          component="span"
                          noWrap
                        >{option.props?.document.name}</Typography>
                        <span title={createdOnFormatted || 'undefined'}>
                          {option.props?.document.numberOfPages ? (
                            <Fragment>
                              {option.props?.document.numberOfPages} {t('labels.pages')}
                              {` • ${createdOnFormatted}`}
                            </Fragment>
                          ) : createdOnFormatted}
                        </span>
                      </Typography>
                      {option.props?.document.labels && option.props.document.labels.length ? (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          {option.props.document.labels.map((label, index) => (
                            <Chip
                              key={`${option.id}-label-${label.name}-${index}`}
                              sx={{
                                borderRadius: 1,
                              }}
                              className={label.color}
                              label={label.name}
                              onClick={undefined}
                              size="small"
                            />
                          ))}
                        </Box>
                      ) : null}
                    </Fragment>
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })
      )}
    </List>
  );
};